import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { clearCart } from "../features/cart/cartSlice";

export default function CheckoutSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth.user);
  const didRun = useRef(false);

  const orderId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return qs.get("orderId");
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    let cancelled = false;

    async function run() {
      if (!orderId) {
        setError(
          "Kein orderId gefunden. ReturnUrl muss ?orderId=... enthalten.",
        );
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const resp = await axios.post(
          `${process.env.BACKEND_URL}/api/payment/saferpay/confirm`,
          { orderId },
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
          },
        );

        if (cancelled) return;

        setPaymentData(resp.data);
        dispatch(clearCart());
      } catch (e) {
        if (cancelled) return;
        console.error("confirm error:", e?.response?.data || e);
        setError(
          "Zahlung konnte nicht bestätigt werden. Bitte Support kontaktieren.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [orderId, dispatch]);

  if (loading) {
    return (
      <div className="kn-page min-h-screen bg-knapp-surface flex items-center justify-center py-8">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <p className="kn-body font-semibold">Zahlung wird bestätigt…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kn-page min-h-screen bg-knapp-surface flex items-center justify-center py-8">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="flex justify-center mb-6">
              <XCircle className="h-16 w-16 text-knapp-accent" />
            </div>
            <h1 className="kn-h1 mb-4">Bestätigung fehlgeschlagen</h1>
            <p className="kn-body mb-6">{error}</p>
            <button
              onClick={() => navigate("/warenkorb")}
              className="kn-btn-ghost w-full"
            >
              Zurück zum Warenkorb
            </button>
          </div>
        </div>
      </div>
    );
  }

  const transactionId = paymentData?.transactionId;

  return (
    <div className="kn-page min-h-screen bg-knapp-surface flex items-center justify-center py-8">
      <div className="max-w-md mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="flex justify-center mb-6">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
          <h1 className="kn-h1 mb-4">Zahlung erfolgreich!</h1>

          {transactionId && (
            <div className="bg-knapp-surface rounded-lg p-4 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-knapp-ink2">Transaktions-ID:</span>
                <span className="font-mono text-xs">{transactionId}</span>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={() => navigate("/bestellungen")}
              className="kn-btn-primary w-full"
            >
              Meine Bestellungen anzeigen
            </button>
            <button
              onClick={() => navigate("/")}
              className="kn-btn-ghost w-full"
            >
              Zurück zum Shop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
